import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

export type BadgeVariant =
  | 'neutral'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'violet'
  | 'teal'
  | 'orange';

export interface BadgeProps {
  variant?: BadgeVariant;
  className?: string;
  children: ReactNode;
}

/**
 * Công thức nhãn trạng thái dùng chung: nền bậc `-50`, chữ bậc `-700` (§9.3.5).
 *
 * Chữ dùng bậc `-700` chứ không phải `-600` để đạt WCAG AA trên nền sáng;
 * `-500`/`-600` chỉ dành cho chấm, thanh và biểu tượng (chỉ cần 3:1).
 *
 * Hệ này **không có** màu `info` riêng — `info` là sky. Xem bí danh
 * `--color-info` / `--color-secondary` trong `globals.css`.
 *
 * ## Hai nhóm biến thể, không trộn vào nhau
 *
 * **Trạng thái** — `success`, `warning`, `danger`, `primary`, `neutral`: mang
 * nghĩa mức độ, thống nhất toàn kho (xem `constants.ts` của từng phân hệ).
 *
 * **Phân loại** — `violet`, `teal`, `orange`, `info`: mang nghĩa *loại*, không
 * phải nặng/nhẹ. Dùng cho giai đoạn luồng việc, nhà cung cấp, vai trò, phòng
 * ban.
 *
 * ❌ Không dùng tông phân loại cho lỗi hay cảnh báo (đã có `danger`,
 * `warning`), và ❌ không dùng tông trạng thái để phân loại — `GLM` tô
 * `success` sẽ đọc thành "nhà cung cấp này đang khỏe".
 */
const VARIANT_CLASS: Record<BadgeVariant, string> = {
  neutral: 'bg-subtle text-content-secondary',
  primary: 'bg-primary-50 text-primary-700',
  success: 'bg-success-50 text-success-700',
  warning: 'bg-warning-50 text-warning-700',
  danger: 'bg-danger-50 text-danger-700',
  info: 'bg-sky-50 text-sky-700',
  violet: 'bg-violet-50 text-violet-700',
  teal: 'bg-teal-50 text-teal-700',
  orange: 'bg-orange-50 text-orange-700',
};

export function Badge({ variant = 'neutral', className, children }: BadgeProps) {
  return (
    <span
      className={cn(
        /*
         * `whitespace-nowrap`: nhãn trạng thái là một cụm từ, không phải một đoạn văn. Trong một ô hẹp mà
         * cho xuống dòng thì "Chưa phát hành" vỡ thành ba dòng chữ xếp dọc trong một viên bo tròn — đọc
         * không ra và đẩy cao cả hàng. Ô hẹp quá thì để nhãn đẩy phần khác co lại (phần ấy có `min-w-0
         * truncate`), chứ nhãn không bao giờ tự bẻ chữ.
         */
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        VARIANT_CLASS[variant],
        className,
      )}
    >
      {children}
    </span>
  );
}
